import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import useFormValidation from 'hooks/useFormValidation';

import { manifestApi, channelApi, shipmentApi } from 'api';

const SHIPPING_METHODS = [
  { value: 'any', label: 'Any' },
  { value: 'cod', label: 'COD' },
  { value: 'prepaid', label: 'Prepaid' },
];

const validationSchema = {
  channel: {
    required: 'Channel is required',
  },
  shippingProvider: {
    required: 'Shipping Provider is required',
  },
  shippingCourier: {
    required: 'Shipping Courier is required',
  },
  shippingMethod: {
    required: 'Shipping method is required',
  },
};

const initialValues = {
  channel: '',
  shippingProvider: '',
  shippingCourier: '',
  shippingMethod: '',
  comments: '',
};

const CreateManifestModal = ({ show, onHide, onSuccess }) => {
  const { selectedWarehouse } = useUserContext();

  const [channels, setChannels] = useState([]);
  const [shippingProviders, setShippingProviders] = useState([]);
  const [shippingCouriers, setShippingCouriers] = useState([]);

  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [isLoadingShippingProviders, setIsLoadingShippingProviders] =
    useState(false);
  const [isLoadingShippingCouriers, setIsLoadingShippingCouriers] =
    useState(false);

  const [channelsError, setChannelsError] = useState(null);
  const [shippingProvidersError, setShippingProvidersError] = useState(null);
  const [shippingCouriersError, setShippingCouriersError] = useState(null);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    isValid,
  } = useFormValidation(initialValues, validationSchema);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!show) return;

      setIsLoadingChannels(true);
      setIsLoadingShippingProviders(true);

      try {
        const [channelResponse, providerResponse] = await Promise.all([
          channelApi.getList(),
          shipmentApi.getShippingProviders({ is_active: true }),
        ]);

        setChannels(channelResponse || []);
        setShippingProviders(providerResponse || []);
      } catch (error) {
        toast.error('Failed to load data');

        setChannelsError('Failed to load channels');
        setShippingProvidersError('Failed to load shipping providers');
      } finally {
        setIsLoadingChannels(false);
        setIsLoadingShippingProviders(false);
      }
    };

    fetchInitialData();
  }, [show]);

  useEffect(() => {
    const fetchShippingCouriers = async () => {
      if (!values.shippingProvider) {
        setShippingCouriers([]);
        return;
      }

      setIsLoadingShippingCouriers(true);
      setShippingCouriersError(null);

      try {
        const response = await shipmentApi.getShippingCouriers(
          values.shippingProvider,
        );
        setShippingCouriers(response || []);
      } catch (error) {
        toast.error('Failed to load shipping couriers');
        setShippingCouriersError('Failed to load shipping couriers');
      } finally {
        setIsLoadingShippingCouriers(false);
      }
    };

    fetchShippingCouriers();
  }, [values.shippingProvider]);

  const handleProviderChange = e => {
    const providerId = e.target.value;
    handleChange('shippingProvider', providerId);
    handleChange('shippingCourier', '');
  };

  const handleCreateManifest = async () => {
    if (!validateForm()) return;

    try {
      await manifestApi.create({
        warehouse: selectedWarehouse?.id,
        channel: Number(values.channel),
        shipping_provider: Number(values.shippingProvider),
        shipping_courier: Number(values.shippingCourier),
        shipping_method: values.shippingMethod.toLowerCase(),
        comments: values.comments,
      });

      toast.success('Manifest created successfully');
      closeModal();
      onSuccess();
    } catch (error) {
      toast.error('Failed to create manifest');
    }
  };

  const closeModal = () => {
    resetForm();
    onHide();
  };

  const renderSelectOptions = (isLoading, error, options, optionName) => {
    if (isLoading) {
      return <option value="">Loading...</option>;
    }
    if (error) {
      return <option value="">{error}</option>;
    }

    return options.map(option => (
      <option key={option.id} value={option.id}>
        {option[optionName]}
      </option>
    ));
  };

  return (
    <Modal show={show} onHide={closeModal}>
      <Modal.Header closeButton>
        <Modal.Title>Create Manifests</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Channel<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.channel}
              onChange={e => handleChange('channel', e.target.value)}
              onBlur={() => handleBlur('channel')}
              isInvalid={touched.channel && errors.channel}
              disabled={isLoadingChannels}
            >
              <option value="">Select Channel...</option>
              {renderSelectOptions(
                isLoadingChannels,
                channelsError,
                channels,
                'channel_type',
              )}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.channel}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Shipping Provider<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.shippingProvider}
              onChange={handleProviderChange}
              onBlur={() => handleBlur('shippingProvider')}
              isInvalid={touched.shippingProvider && errors.shippingProvider}
              disabled={isLoadingShippingProviders}
            >
              <option value="">Select Provider...</option>
              {renderSelectOptions(
                isLoadingShippingProviders,
                shippingProvidersError,
                shippingProviders,
                'shipping_provider_type',
              )}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.shippingProvider}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Shipping Courier<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.shippingCourier}
              onChange={e => handleChange('shippingCourier', e.target.value)}
              onBlur={() => handleBlur('shippingCourier')}
              isInvalid={touched.shippingCourier && errors.shippingCourier}
              disabled={isLoadingShippingCouriers || !values.shippingProvider}
            >
              <option value="">Select Courier...</option>
              {renderSelectOptions(
                isLoadingShippingCouriers,
                shippingCouriersError,
                shippingCouriers,
                'name',
              )}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.shippingCourier}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Shipping Method<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.shippingMethod}
              onChange={e => handleChange('shippingMethod', e.target.value)}
              onBlur={() => handleBlur('shippingMethod')}
              isInvalid={touched.shippingMethod && errors.shippingMethod}
            >
              <option value="">Select Shipping Method...</option>
              {SHIPPING_METHODS.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.shippingMethod}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={values.comments}
              onChange={e => handleChange('comments', e.target.value)}
              placeholder="Enter Comments..."
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={resetForm}>
          Clear
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateManifest}
          disabled={!isValid}
        >
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateManifestModal;
